import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { execute } from '@/graphql/execute';
import { updatePasswordMutation } from '@/mutations/user.mutation';
import { useDialog } from '@/providers/DialogProvider';
import { useFormik } from 'formik';
import { FunctionComponent } from 'react';
import { toast } from 'sonner';

export const ChangePasswordDialog: FunctionComponent<{ userId: string }> = ({ userId }) => {
  const { dismissActiveDialog } = useDialog();

  const formik = useFormik({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validate: (values) => {
      const errors: any = {};
      if (values.newPassword !== values.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
      if (values.newPassword.length < 8) {
        errors.newPassword = 'Password must be at least 8 characters';
      }
      return errors;
    },
    onSubmit: async (_values) => {
      const t = toast.loading('Changing password...');
      try {
        await execute(updatePasswordMutation, {
          id: userId,
          input: {
            currentPassword: _values.currentPassword,
            newPassword: _values.newPassword,
          },
        });
        toast.success('Password changed successfully', { id: t });
      } catch (error) {
        toast.error('Failed to change password', { id: t });
      }
      dismissActiveDialog();
    },
  });

  return (
    <div className='space-y-4'>
      <div className='flex flex-col gap-1'>
        <h3 className='font-medium leading-none'>Change Password</h3>
        <p className='text-sm font-medium leading-none text-slate-500'>Enter your current password and new password.</p>
      </div>
      <form onSubmit={formik.handleSubmit} className='space-y-4'>
        <div className='space-y-2'>
          <Label htmlFor='currentPassword'>Current Password</Label>
          <Input
            id='currentPassword'
            name='currentPassword'
            type='password'
            value={formik.values.currentPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </div>
        <div className='space-y-2'>
          <Label htmlFor='newPassword'>New Password</Label>
          <Input
            id='newPassword'
            name='newPassword'
            type='password'
            value={formik.values.newPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.errors.newPassword && <p className='text-sm text-red-600'>{formik.errors.newPassword}</p>}
        </div>
        <div className='space-y-2'>
          <Label htmlFor='confirmPassword'>Confirm New Password</Label>
          <Input
            id='confirmPassword'
            name='confirmPassword'
            type='password'
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.errors.confirmPassword && <p className='text-sm text-red-600'>{formik.errors.confirmPassword}</p>}
        </div>
        <div className='flex justify-end space-x-2'>
          <Button type='button' variant='outline' onClick={dismissActiveDialog}>
            Cancel
          </Button>
          <Button type='submit' disabled={formik.isSubmitting}>
            Change Password
          </Button>
        </div>
      </form>
    </div>
  );
};
